import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Spinner } from "flowbite-react";

import { useAnalytics } from "../../hooks/useAnalytics";
import { Sidenav } from "../../components";
import { ChurnPageTable } from "./ChurnPageTable";
import { ChurnPageChart } from "./ChurnPageChart";
import { useAuth } from "../../hooks";

const PAGE_SIZE = 10;

export const ChurnPage = () => {
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const { apiClient } = useAuth();

  useEffect(() => {
    trackEvent("churn_dashboard_viewed");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [searchParams] = useSearchParams();

  // Summary data (chart)
  const [summaryData, setSummaryData] = useState(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(true);

  // Accounts data (table with pagination)
  const [accountsData, setAccountsData] = useState(null);
  const [isAccountsLoading, setIsAccountsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false,
  });

  const timeRange = searchParams.get("range") ?? "";

  // Fetch summary data
  const fetchSummaryData = useCallback(() => {
    setIsSummaryLoading(true);
    const params = new URLSearchParams({ time_period: timeRange });
    apiClient
      .get(`/metrics/churn/summary/?${params.toString()}`)
      .then((response) => {
        setSummaryData(response.data);
      })
      .catch((error) => {
        console.error("Error fetching churn summary:", error);
      })
      .finally(() => {
        setIsSummaryLoading(false);
      });
  }, [apiClient, timeRange]);

  useEffect(() => {
    fetchSummaryData();
  }, [fetchSummaryData]);

  // Listen for owner scope changes
  useEffect(() => {
    window.addEventListener("ownerScopeChanged", fetchSummaryData);
    return () => {
      window.removeEventListener("ownerScopeChanged", fetchSummaryData);
    };
  }, [fetchSummaryData]);

  // Fetch accounts data
  const fetchAccounts = useCallback(
    async (page = 1) => {
      setIsAccountsLoading(true);
      try {
        const response = await apiClient.get(
          `/metrics/churn/accounts/?time_period=${timeRange}&page=${page}&page_size=${PAGE_SIZE}`,
        );
        setAccountsData(response.data);
        setPagination({
          page: response.data.pagination?.page || page,
          pageSize: response.data.pagination?.page_size || PAGE_SIZE,
          total: response.data.pagination?.count || 0,
          totalPages: response.data.pagination?.total_pages || 0,
          hasNext: response.data.pagination?.has_next || false,
          hasPrevious: response.data.pagination?.has_previous || false,
        });
      } catch (error) {
        console.error("Error fetching churn accounts:", error);
      } finally {
        setIsAccountsLoading(false);
      }
    },
    [apiClient, timeRange],
  );

  // Fetch accounts on mount and when time range changes
  useEffect(() => {
    fetchAccounts(1);
  }, [fetchAccounts]);

  // Listen for owner scope changes for accounts
  useEffect(() => {
    const handleOwnerScopeChange = () => fetchAccounts(1);
    window.addEventListener("ownerScopeChanged", handleOwnerScopeChange);
    return () => {
      window.removeEventListener("ownerScopeChanged", handleOwnerScopeChange);
    };
  }, [fetchAccounts]);

  const handlePageChange = (newPage) => {
    fetchAccounts(newPage);
  };

  const isInitialLoading = isSummaryLoading && !summaryData;

  return (
    <div className="flex min-h-full flex-1 flex-row">
      <Sidenav />

      {isInitialLoading ? (
        <div className="flex h-screen w-full items-center justify-center">
          <Spinner aria-label="Loading churn data" size="xl" />
        </div>
      ) : (
        <div className="animation-slide-up-lil-bounce flex h-screen w-full justify-center overflow-y-scroll">
          <div className="flex h-full w-[90%] max-w-[1280px] flex-1 flex-col">
            <div className="flex flex-1 flex-col">
              <div className="m-5 inline-flex flex-col items-start justify-start gap-6">
                <div className="text-4xl font-bold leading-10 text-black">
                  {t("pages.churn.title")}
                </div>
              </div>

              <ChurnPageChart
                summary={summaryData?.summary}
                isLoading={isSummaryLoading}
              />
              <ChurnPageTable
                churnedAccounts={accountsData?.churned_accounts}
                dateRange={summaryData?.date_range || accountsData?.date_range}
                pagination={pagination}
                onPageChange={handlePageChange}
                isLoading={isAccountsLoading}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
